// ...existing code...
export interface Point { x: number; y: number; t?: number; }

export const MAX_POINTS = 256;
export const MIN_POINTS = 30;
export const SAMPLE_DISTANCE_PX = 5;
export const RDP_EPS = 1.0;

/** Target distance between consecutive points in normalized [0,1] space.
 *  Derived from mock data analysis (~0.032 average). */
export const TARGET_NORMALIZED_DIST = 0.032;

/** distance */
export function dist(a: Point, b: Point) {
    return Math.hypot(a.x - b.x, a.y - b.y);
}

/** distance based sampling */
export function sampleByDistance(points: Point[], minDist = SAMPLE_DISTANCE_PX) {
    if (!points || points.length === 0) return [];
    const out: Point[] = [points[0]];
    for (let i = 1; i < points.length; i++) {
        if (dist(out[out.length - 1], points[i]) >= minDist) out.push(points[i]);
    }
    return out;
}

/** Ramer-Douglas-Peucker */
export function rdp(points: Point[], epsilon = RDP_EPS): Point[] {
    if (points.length < 3) return points.slice();
    let maxDist = 0;
    let index = 0;
    const a = points[0], b = points[points.length - 1];
    for (let i = 1; i < points.length - 1; i++) {
        const p = points[i];
        const area = Math.abs((b.x - a.x) * (a.y - p.y) - (a.x - p.x) * (b.y - a.y));
        const base = dist(a, b);
        const height = base === 0 ? Math.hypot(p.x - a.x, p.y - a.y) : area / base;
        if (height > maxDist) { maxDist = height; index = i; }
    }
    if (maxDist > epsilon) {
        const left = rdp(points.slice(0, index + 1), epsilon);
        const right = rdp(points.slice(index), epsilon);
        return left.slice(0, -1).concat(right);
    } else {
        return [a, b];
    }
}

/** cumulative length */
export function cumulativeLengths(points: Point[]) {
    const len = [0];
    for (let i = 1; i < points.length; i++) len.push(len[i-1] + dist(points[i-1], points[i]));
    return len;
}

/** linear interpolation */
function lerp(a: number, b: number, t: number) { return a + (b - a) * t; }
function interp(a: Point, b: Point, t: number): Point { return { x: lerp(a.x, b.x, t), y: lerp(a.y, b.y, t) }; }

/** resample to exactly n points along arc-length */
export function resampleToN(points: Point[], n: number) {
    if (n <= 0) return [];
    if (!points || points.length === 0) return Array.from({length:n}, () => ({x:0,y:0}));
    if (points.length === 1) return Array.from({length:n}, () => ({x: points[0].x, y: points[0].y}));
    const len = cumulativeLengths(points);
    const total = len[len.length - 1];
    if (total === 0) return Array.from({length:n}, () => ({...points[0]}));
    const step = total / (n - 1);
    const out: Point[] = [];
    let curIndex = 0;
    for (let i = 0; i < n; i++) {
        const target = Math.min(step * i, total);
        while (curIndex < len.length - 2 && len[curIndex + 1] < target) curIndex++;
        const segLen = len[curIndex + 1] - len[curIndex];
        const t = segLen === 0 ? 0 : (target - len[curIndex]) / segLen;
        out.push(interp(points[curIndex], points[curIndex + 1], t));
    }
    return out;
}

/** normalize points to [0,1] using canvasSize (square) */
export function normalizePoints(points: Point[], canvasSize: number) {
    if (!points) return [];
    return points.map(p => ({ x: p.x / canvasSize, y: p.y / canvasSize }));
}

/** draw black background + white stroke, return dataURL (base64) */
export function drawPreviewBase64(pointsNorm: Point[], strokeWidthPx = 6, outSize = 200) {
    const canvas = document.createElement("canvas");
    canvas.width = outSize; canvas.height = outSize;
    const ctx = canvas.getContext("2d")!;
    // black background
    ctx.fillStyle = "#000"; ctx.fillRect(0, 0, outSize, outSize);
    ctx.strokeStyle = "#fff"; ctx.lineWidth = strokeWidthPx; ctx.lineCap = "round"; ctx.lineJoin = "round";
    if (pointsNorm.length > 0) {
        ctx.beginPath();
        ctx.moveTo(pointsNorm[0].x * outSize, pointsNorm[0].y * outSize);
        for (let i = 1; i < pointsNorm.length; i++) ctx.lineTo(pointsNorm[i].x * outSize, pointsNorm[i].y * outSize);
        ctx.stroke();
    }
    return canvas.toDataURL("image/png");
}